<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import {
  CAMPAIGN_AUDIENCE_TYPES,
  CAMPAIGN_TYPES,
} from 'shared/constants/campaign.js';
import { buildCampaignAudience } from 'shared/helpers/campaignHelper';
import { isVoiceCallEnabled } from 'dashboard/helper/inbox';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CampaignAudienceField from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignAudienceField.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const labels = useMapGetter('labels/getLabels');
const allInboxes = useMapGetter('inboxes/getInboxes');

const initialState = {
  title: '',
  // The line the campaign dials from; it carries the inbox a call falls back to.
  voiceInboxId: null,
  audienceType: CAMPAIGN_AUDIENCE_TYPES.LABEL,
  selectedAudience: [],
  selectedAudienceInboxId: null,
  uploadedContactIds: [],
  maxAttempts: 3,
  retryIntervalHours: 4,
  wrapUpSeconds: 20,
  maxConcurrentLines: 8,
};

const state = reactive({ ...initialState });

const rules = {
  title: { required, minLength: minLength(1) },
  voiceInboxId: { required },
  selectedAudience: {
    required: value => {
      if (state.audienceType === CAMPAIGN_AUDIENCE_TYPES.ALL_CONTACTS)
        return true;
      if (state.audienceType === CAMPAIGN_AUDIENCE_TYPES.INBOX) {
        return !!state.selectedAudienceInboxId;
      }
      if (state.audienceType === CAMPAIGN_AUDIENCE_TYPES.UPLOAD) {
        return state.uploadedContactIds.length > 0;
      }
      return Array.isArray(value) && value.length > 0;
    },
  },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => uiFlags.value.isCreating);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({ value: item[valueKey], label: item[labelKey] })) ?? [];

const voiceInboxes = computed(() =>
  (allInboxes.value || []).filter(isVoiceCallEnabled)
);

const voiceInboxOptions = computed(() =>
  voiceInboxes.value.map(inbox => ({
    value: inbox.id,
    label: inbox.voice_phone_number || inbox.name,
  }))
);

const selectedVoiceInbox = computed(() =>
  voiceInboxes.value.find(inbox => inbox.id === state.voiceInboxId)
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.VOICE.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  voiceNumber: getErrorMessage('voiceInboxId', 'VOICE_NUMBER'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const handleCancel = () => emit('cancel');

const buildAudience = () =>
  buildCampaignAudience({
    audienceType: state.audienceType,
    selectedLabels: state.selectedAudience,
    inboxId: state.selectedAudienceInboxId,
    contactIds: state.uploadedContactIds,
  });

const prepareCampaignDetails = () => ({
  title: state.title,
  campaign_type: CAMPAIGN_TYPES.VOICE_DIALER,
  inbox_id: state.voiceInboxId,
  voice_number_id: selectedVoiceInbox.value?.voice_number_id,
  audience: buildAudience(),
  dialer_config: {
    max_attempts: Number(state.maxAttempts),
    retry_interval_hours: Number(state.retryIntervalHours),
    wrap_up_seconds: Number(state.wrapUpSeconds),
    max_concurrent_lines: Number(state.maxConcurrentLines),
  },
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  Object.assign(state, initialState);
  handleCancel();
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.VOICE.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.VOICE.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label
        for="voice-number"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.VOICE.CREATE.FORM.VOICE_NUMBER.LABEL') }}
      </label>
      <ComboBox
        id="voice-number"
        v-model="state.voiceInboxId"
        :options="voiceInboxOptions"
        :has-error="!!formErrors.voiceNumber"
        :placeholder="t('CAMPAIGN.VOICE.CREATE.FORM.VOICE_NUMBER.PLACEHOLDER')"
        :message="formErrors.voiceNumber"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.VOICE.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <CampaignAudienceField
        v-model:audience-type="state.audienceType"
        v-model:selected-labels="state.selectedAudience"
        v-model:selected-inbox-id="state.selectedAudienceInboxId"
        v-model:uploaded-contact-ids="state.uploadedContactIds"
        :label-options="mapToOptions(labels, 'id', 'title')"
        :inbox-options="mapToOptions(allInboxes, 'id', 'name')"
        :has-error="!!formErrors.audience"
        :error-message="formErrors.audience"
      />
    </div>

    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.VOICE.CREATE.FORM.QUEUE.LABEL') }}
      </label>
      <p class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.VOICE.CREATE.FORM.QUEUE.DESCRIPTION') }}
      </p>
      <div class="grid grid-cols-2 gap-3">
        <Input
          v-model="state.maxAttempts"
          type="number"
          min="1"
          max="5"
          :label="t('CAMPAIGN.VOICE.CREATE.FORM.QUEUE.MAX_ATTEMPTS')"
        />
        <Input
          v-model="state.retryIntervalHours"
          type="number"
          min="1"
          max="72"
          :label="t('CAMPAIGN.VOICE.CREATE.FORM.QUEUE.RETRY_INTERVAL')"
        />
        <Input
          v-model="state.wrapUpSeconds"
          type="number"
          min="0"
          max="300"
          :label="t('CAMPAIGN.VOICE.CREATE.FORM.QUEUE.WRAP_UP')"
        />
        <Input
          v-model="state.maxConcurrentLines"
          type="number"
          min="1"
          max="20"
          :label="t('CAMPAIGN.VOICE.CREATE.FORM.QUEUE.MAX_LINES')"
        />
      </div>
    </div>

    <div class="flex items-center justify-end w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAMPAIGN.VOICE.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.VOICE.CREATE.FORM.BUTTONS.SUBMIT')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isSubmitDisabled || isCreating"
      />
    </div>
  </form>
</template>
